import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {isPlatformBrowser, NgClass} from '@angular/common';
import {BookingService} from '../../services/booking/booking.service';
import {TimeSlot} from '../../../creator/services/calendar/calendar.service';
import {DateTime} from 'luxon';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';

export interface DateTimeSelection {
  date: string;
  dateTime: DateTime;
}

interface CalendarDay {
  date: DateTime;
  label: number;
  key: string;
  inCurrentMonth: boolean;
  disabled: boolean;
  available: boolean;
  selected: boolean;
  today: boolean;
}

interface SlotOption {
  key: string;
  start: DateTime;
  label: string;
}

@Component({
  selector: 'app-date-time-picker',
  standalone: true,
  imports: [NgClass, TranslocoPipe],
  templateUrl: './date-time-picker.component.html',
  styleUrl: './date-time-picker.component.scss'
})
export class DateTimePickerComponent implements OnChanges, OnInit, AfterViewInit {
  @ViewChild('calendarPanel') private calendarPanelRef?: ElementRef<HTMLDivElement>;
  @ViewChild('slotsPanel') private slotsPanelRef?: ElementRef<HTMLDivElement>;
  @Input() serviceId: number | null = null;
  @Input() locale: string | null = null;
  @Input() preventPastNavigation = true;
  @Input() orientation: 'horizontal' | 'vertical' = 'horizontal';
  @Output() dateSelected = new EventEmitter<string>();
  @Output() selectionChange = new EventEmitter<DateTimeSelection>();

  public weekdayLabels: string[] = [];
  public calendarDays: CalendarDay[] = [];
  public timeSlotOptions: SlotOption[] = [];
  public readonly loadingSkeletonItems = Array.from({ length: 42 }, (_, i) => i);

  public isLoadingDays = false;
  public isLoadingSlots = false;
  public noSlotsForDay = false;
  public selectedDate: DateTime | null = null;
  public selectedDateTime: DateTime | null = null;
  public selectedSlotKey: string | null = null;
  public isCollapsed = false;
  public readonly DateTime = DateTime;
  public panelHeight = 0;
  public isMobileLayout = false;

  private availableDays = new Set<string>();
  private displayedMonth = DateTime.local().startOf('month');
  private readonly currentMonth = DateTime.local().startOf('month');
  private readonly isBrowser: boolean;
  private readonly mobileBreakpoint = 680;
  private hasValidatedTodayAvailability = false;

  constructor(
    private readonly bookingService: BookingService,
    private readonly transloco: TranslocoService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.buildWeekdayLabels();
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.updateMobileLayout();
    }
  }

  ngAfterViewInit(): void {
    this.syncPanelHeight();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locale']) {
      this.buildWeekdayLabels();
      this.updateMonthLabel();
    }

    if (changes['serviceId']) {
      this.selectedDate = null;
      this.selectedDateTime = null;
      this.selectedSlotKey = null;
      this.isCollapsed = false;
      this.timeSlotOptions = [];
      this.noSlotsForDay = false;
      this.hasValidatedTodayAvailability = false;

      if (this.serviceId) {
        this.loadAvailableDaysForMonth();
      } else {
        this.calendarDays = [];
      }
    }
  }

  public get monthName(): string {
    return this.displayedMonth
      .setLocale(this.locale ?? this.transloco.getActiveLang())
      .toFormat('LLLL');
  }

  public get yearLabel(): string {
    return this.displayedMonth.toFormat('yyyy');
  }

  public prevMonth(): void {
    if (this.isPrevDisabled) {
      return;
    }
    this.displayedMonth = this.displayedMonth.minus({ months: 1 }).startOf('month');
    this.loadAvailableDaysForMonth();
  }

  public nextMonth(): void {
    this.displayedMonth = this.displayedMonth.plus({ months: 1 }).startOf('month');
    this.loadAvailableDaysForMonth();
  }

  public onSelectDay(day: CalendarDay): void {
    if (day.disabled || !day.available || !this.serviceId) {
      return;
    }
    this.selectedDate = day.date;
    this.selectedDateTime = null;
    this.selectedSlotKey = null;
    this.isCollapsed = false;
    this.markSelectedDay();
    this.dateSelected.emit(day.date.toFormat('yyyy-MM-dd'));
    this.fetchSlotsForDate(day.date);
    this.deferScrollSlotsIntoView();
  }

  public onSelectSlot(slot: SlotOption): void {
    this.selectedSlotKey = slot.key;
    this.selectedDateTime = slot.start;
    this.isCollapsed = true;
    this.selectionChange.emit({
      date: slot.start.toFormat('yyyy-MM-dd'),
      dateTime: slot.start,
    });
  }

  public onEditSelection(): void {
    this.isCollapsed = false;
    this.selectedSlotKey = null;
    this.selectedDateTime = null;
    if (this.selectedDate) {
      this.dateSelected.emit(this.selectedDate.toFormat('yyyy-MM-dd'));
    }
    this.markSelectedDay();
  }

  public get isPrevDisabled(): boolean {
    if (!this.preventPastNavigation) {
      return false;
    }
    const previousMonth = this.displayedMonth.minus({ months: 1 }).startOf('month');
    return previousMonth < this.currentMonth;
  }

  private updateMonthLabel(): void {
    this.displayedMonth = this.displayedMonth.setLocale(this.locale ?? this.transloco.getActiveLang());
    this.buildCalendar();
  }

  private buildWeekdayLabels(): void {
    const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    this.weekdayLabels = dayKeys.map(key => {
      const shortKey = `marketplace.booking.create.date-time-picker.weekdays.${key}`;
      const fallbackKey = `calendar.planning.weekdays.${key}`;
      const value = this.transloco.translate(shortKey);
      const fallback = this.transloco.translate(fallbackKey);
      const base = value !== shortKey ? value : (fallback !== fallbackKey ? fallback : key);
      return base.slice(0, 3).toUpperCase();
    });
  }

  private loadAvailableDaysForMonth(): void {
    if (!this.serviceId) {
      return;
    }
    this.hasValidatedTodayAvailability = false;
    this.isLoadingDays = true;
    const monthStr = this.displayedMonth.toFormat('yyyy-LL');
    this.bookingService.getAvailableDaysForMonth(this.serviceId, monthStr).subscribe({
      next: days => {
        this.availableDays = new Set(days);
        this.buildCalendar();
        this.isLoadingDays = false;
        this.syncPanelHeight();
        this.verifyTodayAvailability();
      },
      error: () => {
        this.availableDays = new Set();
        this.buildCalendar();
        this.isLoadingDays = false;
        this.syncPanelHeight();
      }
    });
  }

  private buildCalendar(): void {
    const monthStart = this.displayedMonth.startOf('month');
    const startOffset = monthStart.weekday - 1;
    const gridStart = monthStart.minus({ days: startOffset });
    const today = DateTime.local().startOf('day');
    const cells: CalendarDay[] = [];

    for (let i = 0; i < 42; i++) {
      const current = gridStart.plus({ days: i });
      const dateStr = current.toFormat('yyyy-MM-dd');
      const inMonth = current.hasSame(monthStart, 'month');
      const available = this.availableDays.has(dateStr);

      cells.push({
        date: current,
        label: current.day,
        key: dateStr,
        inCurrentMonth: inMonth,
        disabled: current < today,
        available,
        selected: this.selectedDate ? current.hasSame(this.selectedDate, 'day') : false,
        today: current.hasSame(today, 'day'),
      });
    }

    this.calendarDays = cells;
    this.queueHeightSync();
  }

  private fetchSlotsForDate(date: DateTime): void {
    if (!this.serviceId) {
      return;
    }
    this.isLoadingSlots = true;
    this.noSlotsForDay = false;
    this.timeSlotOptions = [];
    this.bookingService.getAvailableTimeSlots(this.serviceId, date).subscribe({
      next: response => {
        this.timeSlotOptions = this.buildSlotOptions(response.timeSlots, date);
        this.noSlotsForDay = this.timeSlotOptions.length === 0;
        this.isLoadingSlots = false;
        if (this.noSlotsForDay && this.isToday(date)) {
          this.markDayUnavailable(date);
        }
      },
      error: () => {
        this.noSlotsForDay = true;
        this.isLoadingSlots = false;
      }
    });
  }

  private buildSlotOptions(slots: TimeSlot[], date: DateTime): SlotOption[] {
    const options: SlotOption[] = [];
    const now = DateTime.local();
    const locale = this.locale ?? this.transloco.getActiveLang();

    slots.forEach(slot => {
      const start = DateTime.fromISO(slot.start_utc, { zone: 'utc' }).setZone(DateTime.local().zoneName);
      const end = DateTime.fromISO(slot.end_utc, { zone: 'utc' }).setZone(DateTime.local().zoneName);

      if (!start.hasSame(date, 'day')) return;
      if (start < now) return;

      const key = start.toISO();
      if (!key) return;

      const startLabel = start.setLocale(locale).toLocaleString(DateTime.TIME_SIMPLE);
      options.push({
        key,
        start,
        label: startLabel,
      });
    });

    return options.sort((a, b) => a.start.toMillis() - b.start.toMillis());
  }

  private markSelectedDay(): void {
    this.calendarDays = this.calendarDays.map(day => ({
      ...day,
      selected: this.selectedDate ? day.date.hasSame(this.selectedDate, 'day') : false,
    }));
  }

  public get selectedDateLabel(): string | null {
    if (!this.selectedDateTime) {
      return null;
    }
    const locale = this.locale ?? this.transloco.getActiveLang();
    return this.selectedDateTime
      .setLocale(locale)
      .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
  }

  public get selectedTimeLabel(): string | null {
    if (!this.selectedDateTime) {
      return null;
    }
    const locale = this.locale ?? this.transloco.getActiveLang();
    return this.selectedDateTime
      .setLocale(locale)
      .toLocaleString(DateTime.TIME_SIMPLE);
  }

  private queueHeightSync(): void {
    setTimeout(() => this.syncPanelHeight(), 0);
  }

  private syncPanelHeight(): void {
    const height = this.calendarPanelRef?.nativeElement?.offsetHeight ?? 0;
    if (height && height !== this.panelHeight) {
      this.panelHeight = height;
      this.cdr.detectChanges();
    }
  }

  private updateMobileLayout(): void {
    this.isMobileLayout = this.isBrowser ? window.innerWidth <= this.mobileBreakpoint : false;
  }

  @HostListener('window:resize')
  private onWindowResize(): void {
    if (!this.isBrowser) {
      return;
    }
    const wasMobile = this.isMobileLayout;
    this.updateMobileLayout();
    if (wasMobile !== this.isMobileLayout) {
      this.cdr.detectChanges();
    }
  }

  private deferScrollSlotsIntoView(): void {
    if (!this.isBrowser) {
      return;
    }
    setTimeout(() => this.scrollSlotsIntoViewIfNeeded(), 0);
  }

  private scrollSlotsIntoViewIfNeeded(): void {
    if (!this.isMobileLayout) {
      return;
    }
    const element = this.slotsPanelRef?.nativeElement;
    if (!element) {
      return;
    }
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isFullyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;

    if (!isFullyVisible) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  public get shouldLockPanelHeight(): boolean {
    return this.orientation === 'horizontal' && !this.isMobileLayout;
  }

  private isToday(date: DateTime): boolean {
    return date.hasSame(DateTime.local(), 'day');
  }

  private markDayUnavailable(date: DateTime): void {
    const key = date.toFormat('yyyy-MM-dd');
    this.availableDays.delete(key);
    this.calendarDays = this.calendarDays.map(day =>
      day.key === key
        ? {
            ...day,
            available: false,
            disabled: true,
            selected: false,
          }
        : day
    );
  }

  private verifyTodayAvailability(): void {
    if (!this.serviceId || this.hasValidatedTodayAvailability) {
      return;
    }
    const today = DateTime.local().startOf('day');
    if (!today.hasSame(this.displayedMonth, 'month')) {
      return;
    }
    const todayKey = today.toFormat('yyyy-MM-dd');
    if (!this.availableDays.has(todayKey)) {
      this.hasValidatedTodayAvailability = true;
      return;
    }

    this.bookingService.getAvailableTimeSlots(this.serviceId, today).subscribe({
      next: response => {
        const options = this.buildSlotOptions(response.timeSlots, today);
        if (options.length === 0) {
          this.markDayUnavailable(today);
        }
        this.hasValidatedTodayAvailability = true;
      },
      error: () => {
        this.hasValidatedTodayAvailability = true;
      }
    });
  }
}
